import React, { Component } from 'react';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { incomingStockService } from '../services/incomingStockService';
import './FileUploadDownload.css';

/**
 * FileUploadDownload - Component for handling file upload and template download
 * functionality for incoming stocks
 */
class FileUploadDownload extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            uploadDialogVisible: false,
            uploadProgress: 0,
            uploadedFiles: [],
            isUploading: false,
            uploadErrors: [],
            parsedData: null
        };
        
        this.fileUploadRef = React.createRef();
    }

    // Template download functionality
    downloadTemplate = () => {
        try {
            // Create Excel template with proper headers
            const templateData = this.generateTemplateData();
            const blob = new Blob([templateData], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `incoming_stocks_template_${new Date().getTime()}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download template:', error);
            alert('Failed to download template. Please try again.');
        }
    }

    generateTemplateData = () => {
        // Generate CSV format for Excel template
        const headers = [
            'Supplier Code',
            'Product Name',
            'Quantity',
            'Quantity Type',
            'VAT Category',
            'Net Unit Price',
            'Brutto Unit Price',
            'Net Price',
            'Brutto Price',
            'Expiry Date',
            'Store',
            'Invoice ID',
            'Delivery Note ID',
            'Supplier Name'
        ];
        
        const sampleRow = [
            'CODE001',
            'Sample Product',
            '10',
            'kg',
            'Standard (27%)',
            '100.00',
            '127.00',
            '1000.00',
            '1270.00',
            '2024-12-31',
            'Main Store',
            'INV-2024-001',
            'DN-2024-001',
            'Sample Supplier'
        ];
        
        const csvContent = [
            headers.join(','),
            sampleRow.join(','),
            // Add empty rows for data entry
            ...Array(10).fill(new Array(headers.length).fill('').join(','))
        ].join('\n');
        
        return csvContent;
    }

    // File upload functionality
    showUploadDialog = () => {
        this.setState({ uploadDialogVisible: true });
    }

    hideUploadDialog = () => {
        this.setState({ 
            uploadDialogVisible: false,
            uploadProgress: 0,
            uploadedFiles: [],
            uploadErrors: [],
            parsedData: null
        });
    }

    onFileSelect = (event) => {
        const files = Array.from(event.files);
        this.setState({ uploadedFiles: files });
    }

    onUpload = async () => {
        const { uploadedFiles } = this.state;
        
        if (uploadedFiles.length === 0) {
            alert('Please select a file to upload');
            return;
        }

        this.setState({ isUploading: true, uploadProgress: 0, uploadErrors: [] });

        try {
            for (let i = 0; i < uploadedFiles.length; i++) {
                const file = uploadedFiles[i];
                const progress = ((i + 1) / uploadedFiles.length) * 100;
                
                this.setState({ uploadProgress: progress });
                
                await this.processFile(file);
            }
            
            // If successful, notify parent component
            if (this.props.onUploadSuccess && this.state.parsedData) {
                this.props.onUploadSuccess(this.state.parsedData);
            }
            
        } catch (error) {
            console.error('Upload failed:', error);
            this.setState({ 
                uploadErrors: [error.message || 'Upload failed. Please try again.']
            });
        } finally {
            this.setState({ isUploading: false });
        }
    }

    processFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    const content = e.target.result;
                    const parsedData = this.parseFileContent(file.name, content);
                    
                    this.setState({ parsedData });
                    resolve(parsedData);
                } catch (error) {
                    reject(new Error(`Failed to parse file ${file.name}: ${error.message}`));
                }
            };
            
            reader.onerror = () => {
                reject(new Error(`Failed to read file ${file.name}`));
            };
            
            // Read file based on type
            if (file.type.includes('text') || file.name.toLowerCase().endsWith('.csv')) {
                reader.readAsText(file);
            } else if (file.name.toLowerCase().endsWith('.xlsx')) {
                reader.readAsArrayBuffer(file);
            } else {
                reject(new Error(`Unsupported file type: ${file.type}`));
            }
        });
    }

    parseFileContent = (fileName, content) => {
        if (fileName.toLowerCase().endsWith('.csv')) {
            return this.parseCSV(content);
        } else if (fileName.toLowerCase().endsWith('.xlsx')) {
            return this.parseExcel(content);
        } else {
            throw new Error('Unsupported file format');
        }
    }

    parseCSV = (csvContent) => {
        const lines = csvContent.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        const data = [];

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const values = line.split(',').map(v => v.trim());
            if (values.length !== headers.length) continue;

            const entry = {};
            headers.forEach((header, index) => {
                entry[this.mapHeaderToField(header)] = values[index];
            });

            // Validate and format the entry
            const formattedEntry = this.formatParsedEntry(entry);
            if (formattedEntry) {
                data.push(formattedEntry);
            }
        }

        return {
            entries: data,
            summary: {
                totalRows: lines.length - 1,
                validEntries: data.length,
                skippedRows: (lines.length - 1) - data.length
            }
        };
    }

    parseExcel = (arrayBuffer) => {
        // This would require a library like xlsx to properly parse Excel files
        // For now, we'll return a placeholder
        throw new Error('Excel file parsing requires additional library implementation');
    }

    mapHeaderToField = (header) => {
        const mapping = {
            'Supplier Code': 'supplier_code',
            'Product Name': 'product_name',
            'Quantity': 'count',
            'Quantity Type': 'quantity_type',
            'VAT Category': 'vat_category',
            'Net Unit Price': 'net_unit_price',
            'Brutto Unit Price': 'brutto_unit_price',
            'Net Price': 'net_price',
            'Brutto Price': 'brutto_price',
            'Expiry Date': 'expiry_date',
            'Store': 'store_name',
            'Invoice ID': 'invoice_id',
            'Delivery Note ID': 'delivery_note_id',
            'Supplier Name': 'supplier_name'
        };
        
        return mapping[header] || header.toLowerCase().replace(/\s+/g, '_');
    }

    formatParsedEntry = (entry) => {
        try {
            // Skip empty rows
            if (!entry.product_name || !entry.count) {
                return null;
            }

            return {
                id: `temp-${Date.now()}-${Math.random()}`,
                supplier_code: entry.supplier_code || '',
                count: incomingStockService.parseNumber(entry.count) || 0,
                menuItem: null, // Will need to be resolved from product_name
                product_name: entry.product_name,
                quantityType: entry.quantity_type || '',
                vatCategory: null, // Will need to be resolved from vat_category
                vat_category_name: entry.vat_category || '',
                net_unit_price: incomingStockService.parseNumber(entry.net_unit_price) || 0,
                brutto_unit_price: incomingStockService.parseNumber(entry.brutto_unit_price) || 0,
                net_price: incomingStockService.parseNumber(entry.net_price) || 0,
                brutto_price: incomingStockService.parseNumber(entry.brutto_price) || 0,
                expiry_date: entry.expiry_date ? new Date(entry.expiry_date).getTime() : null,
                store: null, // Will need to be resolved from store_name
                store_name: entry.store_name || '',
                // Additional fields for stock creation
                invoice_id: entry.invoice_id || '',
                delivery_note_id: entry.delivery_note_id || '',
                supplier_name: entry.supplier_name || ''
            };
        } catch (error) {
            console.error('Error formatting entry:', error, entry);
            return null;
        }
    }

    onRemoveFile = (file) => {
        const { uploadedFiles } = this.state;
        const updatedFiles = uploadedFiles.filter(f => f !== file);
        this.setState({ uploadedFiles: updatedFiles });
    }

    render() {
        const { 
            uploadDialogVisible, 
            uploadProgress, 
            uploadedFiles, 
            isUploading, 
            uploadErrors,
            parsedData
        } = this.state;

        const uploadDialogFooter = (
            <div>
                <Button 
                    label="Cancel" 
                    icon="pi pi-times" 
                    onClick={this.hideUploadDialog}
                    className="p-button-text" 
                />
                <Button 
                    label="Upload" 
                    icon="pi pi-upload" 
                    onClick={this.onUpload}
                    disabled={uploadedFiles.length === 0 || isUploading}
                    loading={isUploading}
                />
            </div>
        );

        return (
            <div className="file-upload-download">
                {/* Download Template Button */}
                <button
                    type="button"
                    className="btn btn-danger icon-file-excel"
                    onClick={this.downloadTemplate}
                    title="Download Excel template for bulk import"
                >
                    <span className="browser-only">Download Template</span>
                </button>

                {/* Upload Button */}
                <button
                    type="button"
                    className="btn btn-primary icon-file-excel"
                    onClick={this.showUploadDialog}
                    title="Upload CSV or Excel file with incoming stock data"
                >
                    <span className="browser-only">Upload File</span>
                </button>

                {/* Upload Dialog */}
                <Dialog
                    header="Upload Incoming Stocks"
                    visible={uploadDialogVisible}
                    style={{ width: '50vw' }}
                    footer={uploadDialogFooter}
                    onHide={this.hideUploadDialog}
                >
                    <div className="upload-dialog-content">
                        {/* File Upload Component */}
                        <FileUpload
                            ref={this.fileUploadRef}
                            name="stockFiles"
                            multiple={false}
                            accept=".csv,.xlsx,.xls"
                            maxFileSize={10000000} // 10MB
                            customUpload
                            auto={false}
                            chooseLabel="Choose Files"
                            uploadLabel="Process"
                            cancelLabel="Clear"
                            emptyTemplate={
                                <p className="text-center">Drag and drop CSV or Excel files here to upload.</p>
                            }
                            onSelect={this.onFileSelect}
                            onRemove={this.onRemoveFile}
                        />

                        {/* Upload Progress */}
                        {isUploading && (
                            <div className="upload-progress">
                                <label>Upload Progress</label>
                                <ProgressBar value={uploadProgress} />
                            </div>
                        )}

                        {/* Upload Errors */}
                        {uploadErrors.length > 0 && (
                            <div className="upload-errors">
                                <h4>Upload Errors:</h4>
                                {uploadErrors.map((error, index) => (
                                    <div key={index} className="error-message">
                                        {error}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Parsed Data Summary */}
                        {parsedData && (
                            <div className="parsed-data-summary">
                                <h4>Upload Summary:</h4>
                                <div>Total Rows: {parsedData.summary.totalRows}</div>
                                <div>Valid Entries: {parsedData.summary.validEntries}</div>
                                <div>Skipped Rows: {parsedData.summary.skippedRows}</div>
                                
                                {parsedData.summary.validEntries > 0 && (
                                    <div className="success-message">
                                        {parsedData.summary.validEntries} entries are ready to be imported.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="upload-instructions">
                            <h4>File Format Instructions:</h4>
                            <ul>
                                <li>Supported formats: CSV, Excel (.xlsx, .xls)</li>
                                <li>First row should contain column headers</li>
                                <li>Required columns: Product Name, Quantity</li>
                                <li>Download the template for correct format</li>
                                <li>Maximum file size: 10 MB</li>
                            </ul>
                        </div>
                    </div>
                </Dialog>
            </div>
        );
    }
}

export default FileUploadDownload;